import {
    ChangeDetectionStrategy,
    Component,
    type InputSignal,
    input,
    computed,
    type Signal,
    type OnInit,
    DestroyRef,
    effect,
    inject,
} from '@angular/core';
import { UpperCasePipe } from '@angular/common';
import { Validators, type ValidatorFn, type FormControl } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';
import { CloudValidators } from '@pecan/shared/validators/cloud-validators';
import { DistrictMode, POSTAL_CODE_MAX_LENGTH, ZIP_MAX_LENGTH } from '@pecan/shared/constants/address.constants';
import { TextInputComponent } from '@pecan/shared/components/text-input/text-input.component';
import { postalCodeRegExp } from '@pecan/shared/constants/postal-code-regexp';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [UpperCasePipe, TextInputComponent, TranslatePipe],
    selector: 'app-zip-input',
    styleUrls: ['./zip-input.component.scss'],
    templateUrl: './zip-input.component.html',
})
export class ZipInputComponent implements OnInit {
    public readonly control: InputSignal<FormControl<string>> = input.required<FormControl<string>>();

    public readonly required: InputSignal<boolean> = input<boolean>(true);

    public readonly value: InputSignal<string> = input<string>('');

    public readonly disabled: InputSignal<boolean> = input<boolean>(false);

    public readonly districtMode: InputSignal<DistrictMode> = input<DistrictMode>(DistrictMode.State);

    public readonly DISTRICT_MODE: typeof DistrictMode = DistrictMode;

    public readonly maxLength: Signal<number> = computed(() =>
        this.districtMode() === DistrictMode.State ? ZIP_MAX_LENGTH : POSTAL_CODE_MAX_LENGTH
    );

    private readonly _destroyRef: DestroyRef = inject(DestroyRef);

    constructor() {
        effect(() => this._applyValidators());
    }

    public ngOnInit(): void {
        this._enforceNumericInput();
    }

    private _enforceNumericInput(): void {
        this.control()
            .valueChanges.pipe(
                takeUntilDestroyed(this._destroyRef),
                filter(() => this.districtMode() === DistrictMode.State),
                filter(Boolean),
                map((value: string) => value.replace(/[^0-9]/g, ''))
            )
            .subscribe((value: string) => this.control().setValue(value, { emitEvent: false }));
    }

    private _applyValidators(): void {
        const validators: ValidatorFn[] = [];

        if (this.required()) {
            validators.push(Validators.required);
        }

        const districtMode = this.districtMode();

        if (districtMode === DistrictMode.State) {
            const exactLength = CloudValidators.exactLength(this.maxLength());
            const numericPattern = Validators.pattern(/^[0-9]+$/);
            validators.push(...exactLength, numericPattern);
        } else if (districtMode === DistrictMode.Province) {
            const maxLength = Validators.maxLength(this.maxLength());
            const postalCodePattern = Validators.pattern(postalCodeRegExp);
            validators.push(maxLength, postalCodePattern);
        }

        this.control().setValidators(validators);
        this.control().updateValueAndValidity();
    }
}
